import { codegen } from '@graphql-codegen/core';
import * as typescriptPlugin from '@graphql-codegen/typescript';
import * as typescriptOperationsPlugin from '@graphql-codegen/typescript-operations';
import {
  GraphQLField,
  GraphQLInputType,
  GraphQLInterfaceType,
  GraphQLList,
  GraphQLNonNull,
  GraphQLObjectType,
  GraphQLOutputType,
  GraphQLSchema,
  Source,
  parse,
  print,
  printSchema,
} from 'graphql';
import { TypeMap } from 'graphql/type/schema';
import * as monacoEditor from 'monaco-editor';

import { GraphQLNode } from 'src/lib/graphql/graphqlHelpers';

import { getCurrentDocsContext } from '../explorerDocs/getFieldToggle/getCurrentDocsContext';

import { GraphQLPath } from './operationPathHelpers';

export function printType(type: GraphQLInputType | GraphQLOutputType): string {
  if (type instanceof GraphQLNonNull) {
    return `${printType(type.ofType)}!`;
  }
  if (type instanceof GraphQLList) {
    return `[${printType(type.ofType)}]`;
  }
  return type.name;
}

export type SelectionPathNode =
  | GraphQLField<unknown, unknown>
  | GraphQLObjectType
  | GraphQLInterfaceType;

export function isGraphqlFieldPath(
  path: GraphQLPath,
): path is SelectionPathNode[] {
  return path.every(isGraphqlField);
}
export const isGraphqlField = (
  node: GraphQLNode,
): node is SelectionPathNode => {
  return (
    node instanceof GraphQLObjectType ||
    node instanceof GraphQLInterfaceType ||
    'args' in node
  );
};

const operationDefinitionKinds = new Set([
  'Query',
  'ShortQuery',
  'Mutation',
  'Subscription',
]);
export function isOperationDefinition<
  T extends { kind: string | null | undefined },
>(
  state: T,
): state is T & {
  kind: 'Query' | 'ShortQuery' | 'Mutation' | 'Subscription';
} {
  return !!state.kind && operationDefinitionKinds.has(state.kind);
}

export function getUnusedVariableRanges(
  document: string,
  schema: GraphQLSchema,
) {
  const model = monacoEditor.editor.createModel(document);
  const context = getCurrentDocsContext(
    model,
    'first-non-fragment',
    [],
    schema,
  );
  const unusedVariables = context.variables.filter(
    ({ usageCount }) => usageCount === 0,
  );

  if (unusedVariables.length) {
    if (
      unusedVariables.length === context.variables.length &&
      context.variableDefinitionRange
    ) {
      return [context.variableDefinitionRange];
    }
    return unusedVariables.map(({ range }) => range);
  }

  return [];
}

export function isInternalType(name: string, isSupergraph: boolean) {
  return (
    name.startsWith('__') ||
    (isSupergraph && (name.startsWith('join__') || name.startsWith('link__')))
  );
}

export function typeMapWithoutInternal(
  graphqlSchema: GraphQLSchema,
  isSupergraph: boolean,
): TypeMap {
  return Object.fromEntries(
    Object.entries(graphqlSchema.getTypeMap()).filter(
      ([key]) => !isInternalType(key, isSupergraph),
    ),
  );
}

const GENERATED_TYPESCRIPT_TYPE_PREFIX = `id_`;
const OPERATION_NAME = 'Operation';
export function typescriptTypesByUniqueName(
  uniqueName: string,
  isVariables: boolean,
) {
  return `${GENERATED_TYPESCRIPT_TYPE_PREFIX}${uniqueName
    .replace(/-/g, '_')
    .toLowerCase()}_${OPERATION_NAME}${isVariables ? 'Variables' : ''}`;
}
/**
 * If multiple operations exist in the document, the first one will be used.
 */
export async function graphqlDocumentToTypescriptTypes(
  document: string,
  graphqlSchema: GraphQLSchema,
  includeBoilerPlate: boolean,
  uniqueName: string,
) {
  const parsed = parse(document);
  let foundFirst = false;
  const processedDocument = print({
    ...parsed,
    definitions: parsed.definitions
      .filter((definition) => {
        if (definition.kind === 'OperationDefinition') {
          if (foundFirst) return false;
          foundFirst = true;
        }
        return true;
      })
      .map((definition) => {
        if (definition.kind === 'OperationDefinition') {
          return {
            ...definition,
            name: { kind: 'Name' as const, value: OPERATION_NAME },
          };
        } else {
          return definition;
        }
      }),
  });

  const parsedPrintedSchema = parse(printSchema(graphqlSchema)); // TODO: memo
  return codegen({
    documents: [
      {
        document: parse(new Source(processedDocument, undefined)),
        location: 'path/to',
        rawSDL: processedDocument,
        schema: graphqlSchema,
      },
    ],
    config: {
      enumPrefix: false,
      avoidOptionals: true,
      noExport: true,
      onlyOperationTypes: true,
      typesPrefix: `${GENERATED_TYPESCRIPT_TYPE_PREFIX}${uniqueName
        .replace(/-/g, '_')
        .toLocaleLowerCase()}_`,
    },
    filename: 'placeholder',
    schema: parsedPrintedSchema,
    plugins: [
      ...(includeBoilerPlate ? [{ typescript: {} }] : []),
      {
        'typescript-operations': {
          flattenGeneratedTypes: true,
          omitOperationSuffix: true,
          skipTypename: true,
        },
      },
    ],
    pluginMap: {
      ...(includeBoilerPlate ? { typescript: typescriptPlugin } : {}),
      'typescript-operations': typescriptOperationsPlugin,
    },
  });
}
